import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Check, CheckCircle, Loader2, GripVertical, RotateCcw } from "lucide-react";
import axios from "axios";
import { useGameSocket } from "@/hooks/useGameSocket";
import { useSettingsSync } from "@/hooks/useSettingsSync";
import { TIMER_KEYS } from "@/constants/timers";
import { INNINGS_OPTIONS, BASE_GAME_DEFAULTS } from "@/constants/game";
import GameSettingsPanel from "@/components/setup/GameSettingsPanel";
import { Nav, ConnectionDot } from "@/components/lobby/primitives";
import CreateTab from "@/components/lobby/CreateTab";
import TeamPickerColumn from "@/components/lobby/TeamPickerColumn";
import DeckDraftSection from "@/components/lobby/DeckDraftSection";
import PlayerDraftSection from "@/components/lobby/PlayerDraftSection";
import DraftedDeckModal from "@/components/lobby/DraftedDeckModal";
import DraftedRosterModal from "@/components/lobby/DraftedRosterModal";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { track } from "@/utils/analytics";

// Default position-count requirements when the game record hasn't persisted
// `player_draft_position_counts` yet (just toggled on, never edited). Sum
// (13) drives the per-team pick total; "Flex" is a wildcard slot.
const DEFAULT_POSITION_COUNTS = { P: 3, C: 1, "1B": 1, "2B": 1, "3B": 1, SS: 1, LF: 1, CF: 1, RF: 1, Flex: 2 };
const DEFAULT_DRAFT_ORDER = ["deck_builder", "player"];

// Sortable wrapper for the two draft section blocks. The drag handle is
// rendered into each section's header via the `dragHandle` prop, so the rest
// of the block can stay non-draggable (clicking inside settings shouldn't
// initiate a reorder).
function SortableDraftBlock({ id, children, showHandle, handleDisabled }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const handle = showHandle ? (
    <button
      type="button"
      {...(handleDisabled ? {} : { ...attributes, ...listeners })}
      title={handleDisabled ? "Draft complete — order locked" : "Drag to reorder draft steps"}
      disabled={handleDisabled}
      className={`p-1 rounded touch-none ${handleDisabled ? "opacity-25 cursor-not-allowed text-slate-600" : "hover:bg-slate-800 text-slate-600 hover:text-slate-300 cursor-grab active:cursor-grabbing"}`}
    >
      <GripVertical size={14} />
    </button>
  ) : null;
  return (
    <div ref={setNodeRef} style={style}>
      {typeof children === "function" ? children(handle) : children}
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Nav / ConnectionDot / ColorPicker / SortableSlotRow + roster constants now
// live in @/components/lobby/primitives and @/constants/roster — imported
// above. Future shared lobby UI lands in the same module.

// Mirror of backend `generate_random_weather()` in server.py. Used by BotVsBotSetup
// to preview/re-roll weather client-side before the game exists; the chosen weather
// is sent in the create payload so the server uses it instead of generating a new one.
const WEATHER_CONDITIONS = [
  { condition: "sunny", emoji: "☀️" },
  { condition: "partly cloudy", emoji: "⛅" },
  { condition: "cloudy", emoji: "☁️" },
  { condition: "rainy", emoji: "🌧️" },
];
function generateClientWeather() {
  const c = WEATHER_CONDITIONS[Math.floor(Math.random() * WEATHER_CONDITIONS.length)];
  return { temp: 55 + Math.floor(Math.random() * 48), condition: c.condition, emoji: c.emoji };
}

// PvP setup uses 3-inning default (shorter games); everything else comes from base.
const GAME_DEFAULTS = { ...BASE_GAME_DEFAULTS, innings_total: 3 };


// ─── Helpers ──────────────────────────────────────────────────────────────────
function isFastMode() {
  try { return new URLSearchParams(window.location.search).get("fast") === "1"; }
  catch { return false; }
}


function LocalSetup() {
  const navigate = useNavigate();
  const [gameId, setGameId] = useState(null);
  const [game, setGame] = useState(null);
  const [settings, setSettings] = useState(() => ({ ...GAME_DEFAULTS, fast_mode: isFastMode() }));
  const [awayReady, setAwayReady] = useState(false);
  const [homeReady, setHomeReady] = useState(false);
  const [awayColor, setAwayColor] = useState("gold");
  const [homeColor, setHomeColor] = useState("gold");
  const [starting, setStarting] = useState(false);
  const awayCreateRef = useRef({ canSave: false, hasStarted: false, save: null });
  const homeCreateRef = useRef({ canSave: false, hasStarted: false, save: null });

  useEffect(() => {
    axios.post(`${API}/games/pvp`, {
      away_preset: null, home_preset: null,
      away_name: "Visiting Team", home_name: "Home Team",
      innings_total: 3, fast_mode: isFastMode(),
      excluded_rarities: ["junk_wax"], chip_max: 4,
    }).then(res => { track("game_created_h2h", { mode: "local" }); setGameId(res.data.id); setGame(res.data); })
      .catch(() => toast.error("Couldn't create game session"));
  }, []);

  const rerollWeather = useCallback(async () => {
    if (!gameId) return;
    try { const r = await axios.patch(`${API}/games/${gameId}/settings`, { randomize_weather: true }); setGame(r.data); }
    catch { toast.error("Couldn't update weather"); }
  }, [gameId]);

  // Sync any changed setting to the server. Text/timer fields are debounced 600ms;
  // all others are sent immediately. Replaces 24+ individual useEffect hooks.
  useSettingsSync(gameId, settings);

  const handleSettingChange = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleStart = async () => {
    if (!gameId || !awayReady || !homeReady) return;
    setStarting(true);
    try {
      await axios.post(`${API}/games/${gameId}/start`);
      navigate(`/game/${gameId}`);
    } catch {
      toast.error("Failed to start game");
      setStarting(false);
    }
  };

  const canStart = awayReady && homeReady;

  return (
    <>
      <div className="text-center mb-8">
        <p className="text-xs font-bold text-amber-400 tracking-widest uppercase mb-1">Local Play</p>
        <h1 className="text-3xl font-black">Build your team.</h1>
        <p className="text-slate-500 text-sm mt-1">Name your team, randomize your roster, and get after it.</p>
      </div>

      <div className="mb-6">
        <GameSettingsPanel settings={settings} onSettingChange={handleSettingChange}
          game={game} onRerollWeather={rerollWeather} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
        {/* Away */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Visiting Team</p>
            {awayReady && (
              <span className="flex items-center gap-1 text-[10px] text-emerald-400 font-black">
                <CheckCircle size={10} /> Saved
              </span>
            )}
          </div>
          {gameId ? (
            <CreateTab gameId={gameId} teamType="away" onTeamSaved={() => setAwayReady(true)} stateRef={awayCreateRef} onColorChange={setAwayColor} disabledColor={homeColor} />
          ) : (
            <div className="space-y-2 animate-pulse">
              {[...Array(4)].map((_, i) => <div key={i} className="h-9 rounded-lg bg-slate-800/40" />)}
            </div>
          )}
        </div>

        {/* Home */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Home Team</p>
            {homeReady && (
              <span className="flex items-center gap-1 text-[10px] text-emerald-400 font-black">
                <CheckCircle size={10} /> Saved
              </span>
            )}
          </div>
          {gameId ? (
            <CreateTab gameId={gameId} teamType="home" onTeamSaved={() => setHomeReady(true)} stateRef={homeCreateRef} onColorChange={setHomeColor} disabledColor={awayColor} />
          ) : (
            <div className="space-y-2 animate-pulse">
              {[...Array(4)].map((_, i) => <div key={i} className="h-9 rounded-lg bg-slate-800/40" />)}
            </div>
          )}
        </div>
      </div>

      <button onClick={handleStart} disabled={starting || !canStart || !gameId}
        className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-sm tracking-wide transition-all disabled:opacity-60 flex items-center justify-center gap-2">
        {starting && <Loader2 size={14} className="animate-spin" />}
        {starting ? "Starting…"
          : (!awayReady || !homeReady) ? "Both teams must save their roster first"
          : "Start Game →"}
      </button>
    </>
  );
}

// ─── Online Setup ─────────────────────────────────────────────────────────────
function OnlineSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gameId = searchParams.get("game");
  const role = searchParams.get("role") === "away" ? "away" : "home";
  const isHome = role === "home";

  const [game, setGame] = useState(null);
  const [myReady, setMyReady] = useState(false);
  const [homeReady, setHomeReady] = useState(false);
  const [awayReady, setAwayReady] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigatedRef = useRef(false);

  // ─── Deck Builder Draft state ────────────────────────────────────────────
  // All optional — only meaningful when `game.draft_enabled` is true. Mirrors
  // the server-authoritative `draft_*` fields, plus a few local UI bits
  // (host's pre-start draft settings, the deck-review modal).
  const [draftPhase, setDraftPhase] = useState("idle");
  const [draftReadyHome, setDraftReadyHome] = useState(false);
  const [draftReadyAway, setDraftReadyAway] = useState(false);
  const [deckSize, setDeckSize] = useState(30);
  const [draftFormat, setDraftFormat] = useState("pack");
  const [includedPools, setIncludedPools] = useState(["common", "rare"]);
  const [deckModalOpen, setDeckModalOpen] = useState(false);
  const [rosterModalOpen, setRosterModalOpen] = useState(false);
  const [resetConfirmKind, setResetConfirmKind] = useState(null); // null | "deck_builder" | "player"
  const [cardCatalog, setCardCatalog] = useState({});
  const navigatedToDraftRef = useRef(false);

  const shareUrl = gameId
    ? `${window.location.origin}/online-setup?game=${gameId}&role=away`
    : null;

  // Create game on mount (home player without gameId). `?deck=1` is the
  // Landing easter-egg's deck-builder shortcut — preserved through the
  // redirect so the auto-enable effect below can flip draft_enabled on.
  useEffect(() => {
    if (gameId) return;
    const wantDeckDraft = searchParams.get("deck") === "1";
    axios.post(`${API}/games/online`, { home_name: "Home Team", away_name: "Away Team", fast_mode: isFastMode() })
      .then(res => {
        track("game_created_h2h", { mode: "online", deck_draft: wantDeckDraft });
        const params = new URLSearchParams();
        params.set("game", res.data.id);
        params.set("role", "home");
        if (isFastMode()) params.set("fast", "1");
        if (wantDeckDraft) params.set("deck", "1");
        navigate(`/online-setup?${params}`, { replace: true });
      })
      .catch(() => toast.error("Failed to create session"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMessage = useCallback((msg) => {
    if (msg.type === "game_state") {
      const g = msg.game;
      setGame(g);
      if (g.home_ready) setHomeReady(true);
      if (g.away_ready) setAwayReady(true);
      // Draft state hydration — only meaningful when draft_enabled, but
      // safe to always sync.
      if (g.draft_phase) setDraftPhase(g.draft_phase);
      setDraftReadyHome(!!g.home_draft_ready);
      setDraftReadyAway(!!g.away_draft_ready);
      // Sync draft inputs from the server. Away always mirrors. Host syncs
      // once the draft moves past idle so the host's UI (modal totals,
      // summary pills, etc.) reflects the server-authoritative values that
      // were used at start_draft time — not whatever stale local state
      // they had if they changed selects after the draft was already going.
      const draftStarted = g.draft_phase === "drafting" || g.draft_phase === "complete";
      if (role === "away" || draftStarted) {
        if (typeof g.draft_deck_size === "number") setDeckSize(g.draft_deck_size);
        if (typeof g.draft_format === "string") setDraftFormat(g.draft_format);
        if (Array.isArray(g.draft_pools)) setIncludedPools(g.draft_pools);
      }
    } else if (msg.type === "team_ready") {
      if (msg.role === "home") setHomeReady(msg.ready);
      else setAwayReady(msg.ready);
    } else if (msg.type === "game_start") {
      if (!navigatedRef.current) {
        navigatedRef.current = true;
        navigate(`/game/${gameId}?role=${role}`);
      }
    } else if (msg.type === "draft_started") {
      setDraftPhase(prev => prev === "idle" ? "drafting" : prev);
      // The kickoff broadcast carries the frozen settings — make sure the
      // away client picks them up even if its initial fetch raced.
      if (role === "away") {
        if (typeof msg.deck_size === "number") setDeckSize(msg.deck_size);
        if (typeof msg.format === "string") setDraftFormat(msg.format);
        if (Array.isArray(msg.pools)) setIncludedPools(msg.pools);
      }
    } else if (msg.type === "draft_settings") {
      // Live host-edited settings during idle. Away mirrors; host's local
      // state is authoritative pre-start.
      if (role === "away") {
        if (typeof msg.draft_deck_size === "number") setDeckSize(msg.draft_deck_size);
        if (typeof msg.draft_format === "string") setDraftFormat(msg.draft_format);
        if (Array.isArray(msg.draft_pools)) setIncludedPools(msg.draft_pools);
      }
    } else if (msg.type === "draft_ready") {
      if (msg.role === "home") setDraftReadyHome(!!msg.ready);
      else if (msg.role === "away") setDraftReadyAway(!!msg.ready);
    } else if (msg.type === "draft_state") {
      if (msg.draft_phase === "complete") setDraftPhase("complete");
    } else if (msg.type === "draft_complete") {
      setDraftPhase("complete");
    } else if (msg.type === "player_draft_started") {
      // Host kicked off the player draft. Server flipped phase → drafting
      // and cleared both ready flags; mirror that locally so the away
      // client sees the Ready Up cards immediately.
      setGame(g => g ? {
        ...g,
        player_draft_phase: "drafting",
        home_player_draft_ready: !!msg.home_player_draft_ready,
        away_player_draft_ready: !!msg.away_player_draft_ready,
      } : g);
    } else if (msg.type === "player_draft_ready") {
      setGame(g => g ? { ...g, [`${msg.role}_player_draft_ready`]: !!msg.ready } : g);
    } else if (msg.type === "player_draft_state") {
      // Pool initialized / pick made / pack transitioned. Merge the
      // broadcast fields onto the game record so PvPSetup's derived state
      // (playerPicksActive, etc.) sees the update and triggers nav.
      setGame(g => g ? { ...g, ...msg } : g);
    } else if (msg.type === "player_draft_complete") {
      setGame(g => g ? { ...g, player_draft_phase: "complete" } : g);
    }
  }, [gameId, navigate, role]);

  const { send: wsSend, connected, homeConnected, awayConnected } = useGameSocket(
    gameId || null, role, handleMessage
  );

  useEffect(() => {
    if (!gameId) return;
    axios.get(`${API}/games/${gameId}`).then(r => {
      const g = r.data;
      setGame(g);
      if (g.home_ready) setHomeReady(true);
      if (g.away_ready) setAwayReady(true);
      // Draft hydration (only relevant when draft_enabled, but safe always).
      if (g.draft_phase === "drafting" || g.draft_phase === "complete") setDraftPhase(g.draft_phase);
      if (g.home_draft_ready) setDraftReadyHome(true);
      if (g.away_draft_ready) setDraftReadyAway(true);
      if (role === "away") {
        if (typeof g.draft_deck_size === "number") setDeckSize(g.draft_deck_size);
        if (typeof g.draft_format === "string") setDraftFormat(g.draft_format);
        if (Array.isArray(g.draft_pools)) setIncludedPools(g.draft_pools);
      }
    }).catch(() => {});
  }, [gameId, role]);

  // Card catalog — one-shot fetch for the DraftedDeckModal detail view.
  // Cheap, idempotent endpoint; doing it eagerly keeps the modal snappy.
  useEffect(() => {
    axios.get(`${API}/cards/mock`).then(r => {
      const map = {};
      (r.data || []).forEach(c => { map[c.id] = c; });
      setCardCatalog(map);
    }).catch(() => {});
  }, []);

  // Restore saved game settings from a previous game's "New Game" action (home only).
  useEffect(() => {
    if (!gameId || !isHome) return;
    try {
      const saved = sessionStorage.getItem("junkball_new_game_settings");
      if (!saved) return;
      sessionStorage.removeItem("junkball_new_game_settings");
      axios.patch(`${API}/games/${gameId}/settings`, JSON.parse(saved)).catch(() => {});
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  // Auto-enable the deck-builder draft toggle when arrived via the Landing
  // easter-egg URL (`?deck=1`). Fires once per session — the ref guard keeps
  // it from re-PATCHing on subsequent renders. The PATCH is host-only since
  // only the host can change game settings.
  const draftFlagAppliedRef = useRef(false);
  useEffect(() => {
    if (!gameId || !isHome || !game) return;
    if (draftFlagAppliedRef.current) return;
    if (searchParams.get("deck") !== "1") return;
    draftFlagAppliedRef.current = true;
    if (game.draft_enabled) return;  // already on — nothing to do
    axios.patch(`${API}/games/${gameId}/settings`, { draft_enabled: true }).catch(() => {});
  }, [gameId, isHome, game, searchParams]);

  const doReadyUp = useCallback(() => {
    setMyReady(true);
    if (isHome) setHomeReady(true); else setAwayReady(true);
    wsSend({ type: "team_ready", ready: true });
  }, [isHome, wsSend]);

  const handleReadyToggle = useCallback((selectionType) => {
    if (selectionType === false) {
      setMyReady(false);
      if (isHome) setHomeReady(false); else setAwayReady(false);
      wsSend({ type: "team_ready", ready: false });
      return;
    }
    if (!selectionType) { toast.error("Save your roster first"); return; }
    doReadyUp();
  }, [isHome, wsSend, doReadyUp]);

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Away invite link copied — send to your opponent");
    setTimeout(() => setCopied(false), 2000);
  };

  const patchSettings = useCallback(async (patch) => {
    if (!gameId) return;
    try { await axios.patch(`${API}/games/${gameId}/settings`, patch); }
    catch { toast.error("Couldn't update settings"); }
  }, [gameId]);

  const [ballparkDraft, setBallparkDraft] = useState("");
  const ballparkTimerRef = useRef(null);
  useEffect(() => {
    if (game?.ballpark != null && ballparkDraft === "") setBallparkDraft(game.ballpark || "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.ballpark]);

  const [timerDrafts, setTimerDrafts] = useState(() =>
    Object.fromEntries(TIMER_KEYS.map(k => [k, null]))
  );
  const timerDraftsRef = useRef(timerDrafts);
  const timerPatchTimerRef = useRef(null);
  const timerDraftsInitialized = useRef(false);
  useEffect(() => {
    if (!game || timerDraftsInitialized.current) return;
    timerDraftsInitialized.current = true;
    const init = Object.fromEntries(TIMER_KEYS.map(k => [k, game[k] ?? null]));
    setTimerDrafts(init);
    timerDraftsRef.current = init;
  }, [game]);

  const handleSettingChange = useCallback((key, value) => {
    if (key === "ballpark") {
      setBallparkDraft(value);
      clearTimeout(ballparkTimerRef.current);
      ballparkTimerRef.current = setTimeout(() => patchSettings({ ballpark: value }), 500);
    } else if (TIMER_KEYS.includes(key)) {
      setTimerDrafts(prev => {
        const next = { ...prev, [key]: value };
        timerDraftsRef.current = next;
        return next;
      });
      clearTimeout(timerPatchTimerRef.current);
      timerPatchTimerRef.current = setTimeout(() => patchSettings(timerDraftsRef.current), 600);
    } else {
      patchSettings({ [key]: value });
    }
  }, [patchSettings]);

  // ─── Draft action handlers ───────────────────────────────────────────────

  // Start Draft — host-only. Snapshots current settings so the server can
  // persist + broadcast them; the away client picks them up via draft_started.
  // Also auto-marks the host as draft-ready in the same gesture — clicking
  // "Start Draft" implies "I'm ready", so the second click was redundant.
  // The two WS messages are FIFO on the same connection: server processes
  // start_draft first (flips phase to drafting, clears both ready flags),
  // then draft_ready flips home back to true. Away still confirms.
  const handleStartDraft = useCallback(() => {
    if (!isHome) return;
    wsSend({
      type: "start_draft",
      deck_size: deckSize,
      format: draftFormat,
      pools: includedPools,
    });
    wsSend({ type: "draft_ready", role: "home", ready: true });
    setDraftReadyHome(true);
    setDraftPhase("drafting");
  }, [isHome, wsSend, deckSize, draftFormat, includedPools]);

  const handleConfirmReset = useCallback((kind) => {
    setResetConfirmKind(null);
    if (kind === "deck_builder") {
      wsSend({ type: "reset_draft" });
      setDraftPhase("idle");
      setDraftReadyHome(false);
      setDraftReadyAway(false);
    } else if (kind === "player") {
      wsSend({ type: "reset_player_draft" });
      setGame(g => g ? { ...g, player_draft_phase: "idle", home_player_draft_ready: false, away_player_draft_ready: false } : g);
    }
  }, [wsSend]);

  // Per-player draft ready toggle.
  const handleDraftReadyToggle = useCallback((ready) => {
    if (isHome) setDraftReadyHome(ready); else setDraftReadyAway(ready);
    wsSend({ type: "draft_ready", role, ready });
  }, [isHome, role, wsSend]);

  // Per-player ready toggle for the player draft pre-pool phase. Mirrors
  // handleDraftReadyToggle; the server auto-starts when both sides ready.
  // (`handleStartPlayerDraft` is defined further down so it can close over
  // `playerPositionCounts`, which is derived from `game` after the early
  // `draftEnabled` derivations.)
  const handlePlayerDraftReadyToggle = useCallback((ready) => {
    wsSend({ type: "player_draft_ready", role, ready });
  }, [role, wsSend]);

  // Live-sync host's draft input edits. We send any time the host edits —
  // the server is the authority on whether the change is still accepted
  // (it rejects once the pool is initialized, mirroring the settings-lock
  // UI). The previous `draftPhase === "idle"` gate broke sync when the
  // host unreadied to edit after kicking off the draft: phase was
  // "drafting" but no pool existed yet, so changes were valid but silently
  // dropped, and the away client missed them.
  const handleDeckSizeChange = useCallback((n) => {
    setDeckSize(n);
    if (isHome) wsSend({ type: "draft_settings", deck_size: n });
  }, [isHome, wsSend]);

  const handleDraftFormatChange = useCallback((fmt) => {
    setDraftFormat(fmt);
    if (isHome) wsSend({ type: "draft_settings", format: fmt });
  }, [isHome, wsSend]);

  const handleTogglePool = useCallback((key) => {
    setIncludedPools(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      if (isHome) wsSend({ type: "draft_settings", pools: next });
      return next;
    });
  }, [isHome, wsSend]);

  // Derived: are picks live? Triggers the auto-navigate to /deck-builder-draft.
  const draftEnabled = !!game?.draft_enabled;
  const playerDraftEnabled = !!game?.player_draft_enabled;
  const playerPositionCounts = game?.player_draft_position_counts || DEFAULT_POSITION_COUNTS;
  const picksActive = draftEnabled && draftPhase === "drafting" && (
    (draftReadyHome && draftReadyAway) ||
    (Array.isArray(game?.draft_pool) && game.draft_pool.length > 0) ||
    (Array.isArray(game?.draft_pack) && game.draft_pack.length > 0) ||
    (Array.isArray(game?.draft_picks_home) && game.draft_picks_home.length > 0) ||
    (Array.isArray(game?.draft_picks_away) && game.draft_picks_away.length > 0)
  );

  // Sensors for the draft-order DnD context. PointerSensor with a small
  // activation distance so a click on the handle (without movement) doesn't
  // trigger a phantom drag.
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // Update one position's required count locally + persist via PATCH.
  const handlePositionCountChange = useCallback((pos, count) => {
    const next = { ...(playerPositionCounts || {}), [pos]: count };
    setGame(g => g ? { ...g, player_draft_position_counts: next } : g);
    patchSettings({ player_draft_position_counts: next });
  }, [playerPositionCounts, patchSettings]);

  // Player draft — host kicks off. Mirrors handleStartDraft for the deck
  // draft: flips server phase to drafting (clearing ready flags), then auto-
  // readies the host in the same gesture. Away confirms via Ready Up; when
  // both flags are true the server initializes the pool. Sends position
  // counts inline so a last-second edit reaches the server even if its
  // PATCH hasn't been echoed back yet.
  const handleStartPlayerDraft = useCallback(() => {
    if (!isHome) return;
    wsSend({
      type: "start_player_draft",
      position_counts: playerPositionCounts,
    });
    wsSend({ type: "player_draft_ready", role: "home", ready: true });
    // Optimistic local update so the host's UI immediately reflects the new
    // state — the WS broadcasts will reconcile once they land.
    setGame(g => g ? {
      ...g,
      player_draft_phase: "drafting",
      home_player_draft_ready: true,
      away_player_draft_ready: false,
    } : g);
  }, [isHome, wsSend, playerPositionCounts]);

  // Swap the draft order when the host drags one block past the other.
  const handleDraftOrderDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Refuse reorder once any draft phase has completed.
    if (draftPhase === "complete" || game?.player_draft_phase === "complete") return;
    const persisted = Array.isArray(game?.draft_order) ? game.draft_order : DEFAULT_DRAFT_ORDER;
    const reversed = [...persisted].reverse();
    setGame(g => g ? { ...g, draft_order: reversed } : g);
    patchSettings({ draft_order: reversed });
  }, [draftPhase, game?.draft_order, game?.player_draft_phase, patchSettings]);

  useEffect(() => {
    if (picksActive && draftPhase === "drafting" && gameId && !navigatedToDraftRef.current) {
      navigatedToDraftRef.current = true;
      navigate(`/deck-builder-draft?game=${gameId}&role=${role}`);
    }
    if (draftPhase === "idle") {
      navigatedToDraftRef.current = false;
    }
    if (draftPhase === "complete") {
      navigatedToDraftRef.current = false;
      track("draft_completed", {
        deck_size: deckSize,
        pools: includedPools.join(","),
        format: draftFormat,
      });
    }
  }, [picksActive, draftPhase, gameId, role, navigate, deckSize, includedPools, draftFormat]);

  // Player draft auto-nav. Trips once `player_draft_pool` has been initialized
  // (server has flipped phase → drafting and pre-dealt the first pack).
  const playerDraftPhase = game?.player_draft_phase || "idle";
  const playerPicksActive = playerDraftEnabled && playerDraftPhase === "drafting" && (
    (Array.isArray(game?.player_draft_pool) && game.player_draft_pool.length > 0) ||
    (Array.isArray(game?.player_draft_pack) && game.player_draft_pack.length > 0) ||
    (Array.isArray(game?.player_draft_picks_home) && game.player_draft_picks_home.length > 0) ||
    (Array.isArray(game?.player_draft_picks_away) && game.player_draft_picks_away.length > 0)
  );
  const navigatedToPlayerDraftRef = useRef(false);
  useEffect(() => {
    if (playerPicksActive && playerDraftPhase === "drafting" && gameId && !navigatedToPlayerDraftRef.current) {
      navigatedToPlayerDraftRef.current = true;
      navigate(`/player-draft?game=${gameId}&role=${role}`);
    }
    if (playerDraftPhase === "idle" || playerDraftPhase === "complete") {
      navigatedToPlayerDraftRef.current = false;
    }
  }, [playerPicksActive, playerDraftPhase, gameId, role, navigate]);

  if (!gameId) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-sm text-slate-400">Setting up session…</span>
      </div>
    );
  }

  const bothReady = homeReady && awayReady;
  const opponentReady = isHome ? awayReady : homeReady;

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <p className="text-xs font-bold text-blue-400 tracking-widest uppercase mb-1">Head to Head (Online)</p>
          <h1 className="text-3xl font-black text-white">Build your team.</h1>
          <p className="text-slate-400 text-sm mt-1.5 max-w-sm leading-relaxed">
            Name your team, randomize your roster, and ready up. Both players must ready up before the game begins.
          </p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-1.5 pt-1 flex-shrink-0">
          <ConnectionDot connected={homeConnected} label="Home" />
          <ConnectionDot connected={awayConnected} label="Away" />
        </div>
      </div>

      {/* Step indicator — adapts to role and whether draft is enabled */}
      {(() => {
        const steps = [
          { label: "Session ready",    done: !!game },
          ...(isHome ? [{ label: "Invite opponent", done: awayConnected }] : []),
          ...(draftEnabled ? [{ label: "Draft", done: draftPhase === "complete" }] : []),
          { label: "Build your roster", done: myReady },
          { label: "Play ball",         done: bothReady },
        ];
        return (
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider flex-wrap">
            {steps.map((step, i) => (
              <span key={step.label} className="flex items-center gap-2">
                {i > 0 && <span className="text-slate-800">─</span>}
                <span className={step.done ? "text-emerald-400" : "text-slate-500"}>
                  {step.done ? "✓" : `${i + 1}.`} {step.label}
                </span>
              </span>
            ))}
          </div>
        );
      })()}

      {/* Share link (home only) */}
      {isHome && shareUrl && (
        <div className="p-4 rounded-2xl border border-slate-700 bg-slate-900/60">
          <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
            Invite Your Opponent
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[10px] text-amber-400 bg-slate-800 px-3 py-2 rounded-lg truncate font-mono">
              {shareUrl}
            </code>
            <button onClick={handleCopy}
              className="flex-shrink-0 p-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black transition-colors">
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
          {!awayConnected && (
            <p className="text-[10px] text-slate-600 mt-2">Waiting for away player to open the link…</p>
          )}
        </div>
      )}

      {/* Away: connecting banner */}
      {!isHome && !homeConnected && (
        <div className="p-3 rounded-xl border border-slate-700 bg-slate-900/40 text-center">
          <p className="text-xs text-slate-500">Connecting to session…</p>
        </div>
      )}

      {/* Game settings */}
      {game && (
        <GameSettingsPanel
          settings={{
            innings_total: game.innings_total || 3,
            ballpark: ballparkDraft,
            draft_enabled: !!game.draft_enabled,
            excluded_rarities: game.excluded_rarities || ["junk_wax"],
            chip_max: game.chip_max || 4,
            pitch_mod: game.pitch_mod ?? 2,
            hand_size: game.hand_size ?? 5,
            fast_mode: !!game.fast_mode,
            no_timers: !!game.no_timers,
            require_chip_match_for_hr: !!game.require_chip_match_for_hr,
            depleting_chips: !!game.depleting_chips,
            chip_arsenal: !!game.chip_arsenal,
            chip_arsenal_infinite: game.chip_arsenal_infinite ?? 1,
            chip_arsenal_modifiers: game.chip_arsenal_modifiers ?? [1, 1, 1, 1],
            chip_arsenal_fb_count: game.chip_arsenal_fb_count ?? 8,
            chip_arsenal_ch_count: game.chip_arsenal_ch_count ?? 5,
            chip_arsenal_cb_count: game.chip_arsenal_cb_count ?? 3,
            chip_arsenal_sl_count: game.chip_arsenal_sl_count ?? 2,
            continuous_draw: !!game.continuous_draw,
            heat_map: !!game.heat_map,
            heat_map_overlap_bonus: game.heat_map_overlap_bonus ?? 1,
            heat_map_chip_bonus: game.heat_map_chip_bonus ?? 1,
            same_raw_tie_is_ball: game.same_raw_tie_is_ball ?? false,
            modifier_tie_is_ball: game.modifier_tie_is_ball ?? false,
            any_raw_tie_is_ball: !!game.any_raw_tie_is_ball,
            called_ball_tie_is_strike: !!game.called_ball_tie_is_strike,
            bluff_values: !!game.bluff_values,
            unlimited_stacking: !!game.unlimited_stacking,
            player_draft_enabled: !!game.player_draft_enabled,
            ...timerDrafts,
          }}
          onSettingChange={handleSettingChange}
          disabled={!isHome}
          showPitchMod
          // When the deck-builder draft is on, the rarity pool is chosen
          // downstream in the Deck Draft section — surface the Manager Card
          // Deck row in read-only "draft-locked" mode so it can't drift from
          // the draft pools the players actually used.
          rarityMode={game.draft_enabled ? "draft-locked" : "editable"}
          game={game}
          onRerollWeather={() => patchSettings({ randomize_weather: true })}
        />
      )}

      {/* Draft steps — one block per enabled draft, rendered in the host's
          `draft_order`. When both are on, the host can drag the GripVertical
          handle in each section's header to swap which runs first. */}
      {game && (draftEnabled || playerDraftEnabled) && (() => {
        const persistedOrder = Array.isArray(game?.draft_order) ? game.draft_order : DEFAULT_DRAFT_ORDER;
        // Render in persisted order, but filter to only the kinds whose
        // toggle is actually on right now.
        const enabledByKind = { deck_builder: draftEnabled, player: playerDraftEnabled };
        const completeByKind = {
          deck_builder: draftPhase === "complete",
          player: game?.player_draft_phase === "complete",
        };
        // Completed phases always float to the top; drag is locked once any
        // phase finishes (order is effectively decided at that point).
        const anyComplete = Object.values(completeByKind).some(Boolean);
        const orderedKinds = persistedOrder
          .filter(k => enabledByKind[k])
          .sort((a, b) => (completeByKind[b] ? 1 : 0) - (completeByKind[a] ? 1 : 0));
        const showHandle = isHome && draftEnabled && playerDraftEnabled;

        const buildHandle = (kind, handle) => {
          const isComplete = completeByKind[kind];
          const resetBtn = isHome && isComplete ? (
            <button
              type="button"
              onClick={() => setResetConfirmKind(kind)}
              title="Reset this draft phase"
              className="p-1 rounded hover:bg-slate-800 text-slate-500 hover:text-amber-400 transition-colors"
            >
              <RotateCcw size={14} />
            </button>
          ) : null;
          return (resetBtn || handle) ? (
            <div className="flex items-center gap-0.5">
              {resetBtn}
              {handle}
            </div>
          ) : null;
        };

        const renderBlock = (kind, handle) => {
          const combinedHandle = buildHandle(kind, handle);
          if (kind === "deck_builder") {
            return (
              <DeckDraftSection
                deckSize={deckSize}
                onDeckSize={handleDeckSizeChange}
                includedPools={includedPools}
                onTogglePool={handleTogglePool}
                draftPhase={draftPhase}
                disabled={!isHome}
                isHome={isHome}
                canStartDraft={includedPools.length > 0}
                onStartDraft={handleStartDraft}
                role={role}
                draftReadyHome={draftReadyHome}
                draftReadyAway={draftReadyAway}
                onDraftReady={handleDraftReadyToggle}
                draftFormat={draftFormat}
                onDraftFormat={handleDraftFormatChange}
                draftDeckMode={game.draft_deck_mode || "shared"}
                onDraftDeckMode={(v) => handleSettingChange("draft_deck_mode", v)}
                picksActive={picksActive}
                onViewDeck={() => setDeckModalOpen(true)}
                deckCardCount={(game?.draft_picks_home?.length ?? 0) + (game?.draft_picks_away?.length ?? 0)}
                dragHandle={combinedHandle}
              />
            );
          }
          if (kind === "player") {
            return (
              <PlayerDraftSection
                positionCounts={playerPositionCounts}
                onPositionCountChange={handlePositionCountChange}
                playerDraftPhase={game?.player_draft_phase || "idle"}
                disabled={!isHome}
                isHome={isHome}
                role={role}
                canStartDraft={true}
                onStartDraft={handleStartPlayerDraft}
                draftReadyHome={!!game?.home_player_draft_ready}
                draftReadyAway={!!game?.away_player_draft_ready}
                onDraftReady={handlePlayerDraftReadyToggle}
                picksActive={playerPicksActive}
                dragHandle={combinedHandle}
                onViewRosters={() => setRosterModalOpen(true)}
              />
            );
          }
          return null;
        };

        if (orderedKinds.length === 1) {
          // Single block — no DnD context needed, also no handle.
          return renderBlock(orderedKinds[0], null);
        }

        return (
          <DndContext
            sensors={dndSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDraftOrderDragEnd}
          >
            <SortableContext items={orderedKinds} strategy={verticalListSortingStrategy}>
              {orderedKinds.map(kind => (
                <SortableDraftBlock key={kind} id={kind} showHandle={showHandle} handleDisabled={completeByKind[kind]}>
                  {(handle) => renderBlock(kind, handle)}
                </SortableDraftBlock>
              ))}
            </SortableContext>
          </DndContext>
        );
      })()}

      {/* Two-column team pickers. When the draft step is enabled, both
          columns gate themselves behind `draftComplete` so the lobby reads
          as draft → roster, not draft + roster in parallel. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
        <TeamPickerColumn
          label="Home Team"
          accent="home"
          isMe={isHome}
          isReady={homeReady}
          gameId={gameId}
          teamType="home"
          game={game}
          onReadyToggle={handleReadyToggle}
          connected={connected}
          draftComplete={
            (!draftEnabled || draftPhase === "complete") &&
            (!playerDraftEnabled || game?.player_draft_phase === "complete")
          }
        />
        <TeamPickerColumn
          label="Away Team"
          accent="away"
          isMe={!isHome}
          isReady={awayReady}
          gameId={gameId}
          teamType="away"
          game={game}
          onReadyToggle={handleReadyToggle}
          connected={connected}
          draftComplete={
            (!draftEnabled || draftPhase === "complete") &&
            (!playerDraftEnabled || game?.player_draft_phase === "complete")
          }
        />
      </div>

      {/* Both ready banner */}
      {bothReady && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-center">
          <p className="text-emerald-400 font-black text-lg animate-pulse">⚾ Play Ball!</p>
          <p className="text-slate-400 text-xs mt-1">Both players ready — launching the game…</p>
        </div>
      )}

      {/* Opponent queued nudge */}
      {opponentReady && !myReady && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-center">
          <p className="text-amber-400 text-xs font-bold">Your opponent is ready — save your roster and ready up!</p>
        </div>
      )}

      {/* Post-draft deck review modal — only opened from DeckDraftSection's
          "View Deck" CTA, but mounted unconditionally so it can fade out
          cleanly if `draft_enabled` toggles off mid-render. */}
      <DraftedDeckModal
        open={deckModalOpen}
        onClose={() => setDeckModalOpen(false)}
        deck={(game?.draft_picks_home || []).concat(game?.draft_picks_away || [])}
        catalog={cardCatalog}
        totalCards={game?.draft_deck_size ?? deckSize}
      />

      {/* Post-player-draft roster review modal. Pulls directly from the
          team records the server wrote in `_apply_player_draft_to_rosters`
          so the trimmed-vintage stat lines + filler tags survive the trip. */}
      <DraftedRosterModal
        open={rosterModalOpen}
        onClose={() => setRosterModalOpen(false)}
        homeTeam={game?.home_team}
        awayTeam={game?.away_team}
        role={role}
        gameId={gameId}
      />

      {/* Draft reset confirmation dialog */}
      {resetConfirmKind && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setResetConfirmKind(null)}
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-sm p-6 shadow-2xl space-y-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="space-y-1">
              <p className="font-black text-white text-base">
                Reset {resetConfirmKind === "deck_builder" ? "Deck Draft" : "Player Draft"}?
              </p>
              <p className="text-xs text-slate-400 leading-relaxed">
                All picks will be cleared and the phase will return to idle.
                Your settings ({resetConfirmKind === "deck_builder" ? "deck size, card pool, format" : "position counts"}) will be kept.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setResetConfirmKind(null)}
                className="flex-1 px-4 py-2 rounded-xl border border-slate-700 bg-slate-800 text-slate-300 hover:text-white text-xs font-black transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleConfirmReset(resetConfirmKind)}
                className="flex-1 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-black transition-colors"
              >
                Reset Draft
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Bot Setup ────────────────────────────────────────────────────────────────
function BotSetup() {
  const navigate = useNavigate();
  const [gameId, setGameId] = useState(null);
  const [game, setGame] = useState(null);
  const [settings, setSettings] = useState(() => {
    // vs-Bot defaults to No Timers: there's no human opponent waiting on you, and
    // the 30s pitch clock is punishing for slower players and automated ones alike.
    // Host can still switch it back on in Enhanced Mechanics. Scoped to this setup
    // screen on purpose — GAME_DEFAULTS is shared with local PvP and bot-vs-bot.
    const defaults = { ...GAME_DEFAULTS, no_timers: true, fast_mode: isFastMode() };
    try {
      const saved = sessionStorage.getItem("junkball_new_game_settings");
      if (saved) {
        sessionStorage.removeItem("junkball_new_game_settings");
        return { ...defaults, ...JSON.parse(saved), fast_mode: isFastMode() };
      }
    } catch {}
    return defaults;
  });
  const [homeReady, setHomeReady] = useState(false);
  const [savedName, setSavedName] = useState("");
  const [starting, setStarting] = useState(false);
  const homeCreateRef = useRef({ canSave: false, hasStarted: false, save: null });

  useEffect(() => {
    axios.post(`${API}/games/bot-setup`, {
      innings_total: 3, fast_mode: isFastMode(), no_timers: true,
      excluded_rarities: ["junk_wax"], chip_max: 4,
    }).then(res => { track("game_created_vs_bot", {}); setGameId(res.data.id); setGame(res.data); })
      .catch(() => toast.error("Couldn't create bot game session"));
  }, []);

  const rerollWeather = useCallback(async () => {
    if (!gameId) return;
    try { const r = await axios.patch(`${API}/games/${gameId}/settings`, { randomize_weather: true }); setGame(r.data); }
    catch { toast.error("Couldn't update weather"); }
  }, [gameId]);

  // Sync discrete settings immediately
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { innings_total: settings.innings_total }).catch(() => {}); }, [gameId, settings.innings_total]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { excluded_rarities: settings.excluded_rarities }).catch(() => {}); }, [gameId, settings.excluded_rarities]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { chip_max: settings.chip_max }).catch(() => {}); }, [gameId, settings.chip_max]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { pitch_mod: settings.pitch_mod }).catch(() => {}); }, [gameId, settings.pitch_mod]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { hand_size: settings.hand_size }).catch(() => {}); }, [gameId, settings.hand_size]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { fast_mode: settings.fast_mode }).catch(() => {}); }, [gameId, settings.fast_mode]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { no_timers: settings.no_timers }).catch(() => {}); }, [gameId, settings.no_timers]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { require_chip_match_for_hr: settings.require_chip_match_for_hr }).catch(() => {}); }, [gameId, settings.require_chip_match_for_hr]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { depleting_chips: settings.depleting_chips }).catch(() => {}); }, [gameId, settings.depleting_chips]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { chip_arsenal: settings.chip_arsenal }).catch(() => {}); }, [gameId, settings.chip_arsenal]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { chip_arsenal_infinite: settings.chip_arsenal_infinite }).catch(() => {}); }, [gameId, settings.chip_arsenal_infinite]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { chip_arsenal_modifiers: settings.chip_arsenal_modifiers }).catch(() => {}); }, [gameId, settings.chip_arsenal_modifiers]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { chip_arsenal_fb_count: settings.chip_arsenal_fb_count }).catch(() => {}); }, [gameId, settings.chip_arsenal_fb_count]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { chip_arsenal_ch_count: settings.chip_arsenal_ch_count }).catch(() => {}); }, [gameId, settings.chip_arsenal_ch_count]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { chip_arsenal_cb_count: settings.chip_arsenal_cb_count }).catch(() => {}); }, [gameId, settings.chip_arsenal_cb_count]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { chip_arsenal_sl_count: settings.chip_arsenal_sl_count }).catch(() => {}); }, [gameId, settings.chip_arsenal_sl_count]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { continuous_draw: settings.continuous_draw }).catch(() => {}); }, [gameId, settings.continuous_draw]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { heat_map: settings.heat_map }).catch(() => {}); }, [gameId, settings.heat_map]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { heat_map_overlap_bonus: settings.heat_map_overlap_bonus }).catch(() => {}); }, [gameId, settings.heat_map_overlap_bonus]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { heat_map_chip_bonus: settings.heat_map_chip_bonus }).catch(() => {}); }, [gameId, settings.heat_map_chip_bonus]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { heat_map_overhang_threshold: settings.heat_map_overhang_threshold }).catch(() => {}); }, [gameId, settings.heat_map_overhang_threshold]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { heat_map_zone_width_pct: settings.heat_map_zone_width_pct }).catch(() => {}); }, [gameId, settings.heat_map_zone_width_pct]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { same_raw_tie_is_ball: settings.same_raw_tie_is_ball }).catch(() => {}); }, [gameId, settings.same_raw_tie_is_ball]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { modifier_tie_is_ball: settings.modifier_tie_is_ball }).catch(() => {}); }, [gameId, settings.modifier_tie_is_ball]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { any_raw_tie_is_ball: settings.any_raw_tie_is_ball }).catch(() => {}); }, [gameId, settings.any_raw_tie_is_ball]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { called_ball_tie_is_strike: settings.called_ball_tie_is_strike }).catch(() => {}); }, [gameId, settings.called_ball_tie_is_strike]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { bluff_values: settings.bluff_values }).catch(() => {}); }, [gameId, settings.bluff_values]);
  useEffect(() => { if (gameId) axios.patch(`${API}/games/${gameId}/settings`, { unlimited_stacking: settings.unlimited_stacking }).catch(() => {}); }, [gameId, settings.unlimited_stacking]);

  // Debounced text field
  useEffect(() => {
    if (!gameId) return;
    const t = setTimeout(() => axios.patch(`${API}/games/${gameId}/settings`, { ballpark: settings.ballpark.trim() }).catch(() => {}), 500);
    return () => clearTimeout(t);
  }, [gameId, settings.ballpark]);

  // Debounced timer fields
  const { pitch_clock_secs, pitch_pitcher_secs, pitch_batter_secs, fielding_clock_secs, steal_clock_secs, hand_refresh_secs, pickoff_select_secs,
          card_modal_secs, swap_spit_secs, draft_pick_secs, player_draft_pick_secs } = settings;
  useEffect(() => {
    if (!gameId) return;
    const t = setTimeout(() => {
      axios.patch(`${API}/games/${gameId}/settings`, {
        pitch_clock_secs, pitch_pitcher_secs, pitch_batter_secs, fielding_clock_secs, steal_clock_secs, hand_refresh_secs, pickoff_select_secs,
        card_modal_secs, swap_spit_secs, draft_pick_secs, player_draft_pick_secs,
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [gameId, pitch_clock_secs, pitch_pitcher_secs, pitch_batter_secs, fielding_clock_secs, steal_clock_secs, hand_refresh_secs, pickoff_select_secs,
      card_modal_secs, swap_spit_secs, draft_pick_secs, player_draft_pick_secs]);

  const handleSettingChange = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);


  const handleStart = async () => {
    if (!gameId || starting) return;
    if (!homeReady) { toast.error("Save your roster first"); return; }
    setStarting(true);
    try {
      await axios.post(`${API}/games/${gameId}/start`);
      navigate(`/game/${gameId}?role=home${isFastMode() ? "&fast=1" : ""}`);
    } catch {
      toast.error("Failed to start game");
      setStarting(false);
    }
  };

  const canStart = homeReady;

  return (
    <>
      <div className="text-center mb-8">
        <p className="text-xs font-bold text-red-400 tracking-widest uppercase mb-1">Vs Bot</p>
        <h1 className="text-3xl font-black">Build your team.</h1>
        <p className="text-slate-500 text-sm mt-1">Set up your roster and settings, then face the machine.</p>
      </div>

      <div className="mb-6">
        <GameSettingsPanel settings={settings} onSettingChange={handleSettingChange} showPitchMod
          game={game} onRerollWeather={rerollWeather}
          // Draft flows (deck builder, player draft) aren't wired up for
          // bot mode yet — bots have no pick strategy, so flipping these
          // on would softlock the lobby waiting on a pick that never
          // comes. Grey out the toggles with a "not supported" caption.
          draftsSupported={false} />
      </div>

      {/* Home team builder */}
      <div className="space-y-3 mb-4">
        <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Your Team (Home)</p>
        {!gameId ? (
          <div className="space-y-2 animate-pulse">
            {[...Array(4)].map((_, i) => <div key={i} className="h-9 rounded-lg bg-slate-800/40" />)}
          </div>
        ) : homeReady ? (
          <div className="space-y-2">
            <div className="rounded-xl border border-amber-500 bg-amber-500/10 p-3.5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-black text-sm text-white">{savedName}</p>
                  <p className="text-[9px] font-bold uppercase tracking-widest mt-0.5 text-amber-400">Custom Roster · Saved</p>
                </div>
                <CheckCircle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
              </div>
            </div>
            <button onClick={() => setHomeReady(false)}
              className="w-full text-[10px] text-slate-500 hover:text-slate-300 py-1 transition-colors">
              Edit roster ↻
            </button>
          </div>
        ) : (
          <CreateTab gameId={gameId} teamType="home"
            onTeamSaved={(name) => { setSavedName(name); setHomeReady(true); }}
            stateRef={homeCreateRef} />
        )}
      </div>

      {/* Bot opponent card */}
      <div className="rounded-xl border border-red-900/40 bg-red-950/20 p-3.5 mb-6 flex items-center gap-3">
        <span className="text-2xl select-none">🤖</span>
        <div>
          <p className="text-sm font-black text-white">Wax Packers</p>
          <p className="text-[10px] text-red-400/70 font-bold uppercase tracking-widest">Bot Opponent · Away</p>
        </div>
      </div>

      <button onClick={handleStart} disabled={starting || !canStart || !gameId}
        className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-black text-sm tracking-wide transition-all disabled:opacity-60 flex items-center justify-center gap-2">
        {starting && <Loader2 size={14} className="animate-spin" />}
        {starting ? "Starting…"
          : !homeReady ? "Save your roster first"
          : "Start vs Bot →"}
      </button>

    </>
  );
}

const SIM_SPEEDS = [
  { label: "Plaid",   delay: 0.0,  desc: "No pause — instant"      },
  { label: "Turbo",  delay: 0.1,  desc: "Blazing fast"            },
  { label: "Fast",   delay: 0.5,  desc: "Quick but followable"    },
  { label: "Normal", delay: 1.5,  desc: "Comfortable to watch"    },
  { label: "Slow",   delay: 3.0,  desc: "Leisurely, every detail" },
];

function BotVsBotSetup() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(() => {
    const defaults = { ...GAME_DEFAULTS, fast_mode: isFastMode() };
    try {
      const saved = sessionStorage.getItem("junkball_new_game_settings");
      if (saved) {
        sessionStorage.removeItem("junkball_new_game_settings");
        return { ...defaults, ...JSON.parse(saved), fast_mode: isFastMode() };
      }
    } catch {}
    return defaults;
  });
  const [simSpeedIdx, setSimSpeedIdx] = useState(2); // default: Fast
  const [starting, setStarting] = useState(false);
  // Per-side bot skill tier — shipped as home_difficulty / away_difficulty.
  const [homeDiff, setHomeDiff] = useState("normal");
  const [awayDiff, setAwayDiff] = useState("normal");
  // Weather picked at setup time. Game isn't persisted until Start, so we keep the
  // choice locally and ship it in the create payload.
  const [weather, setWeather] = useState(() => generateClientWeather());

  const handleSettingChange = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleStart = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const payload = {
        ...settings,
        ballpark: settings.ballpark.trim() || undefined,
        sim_delay: SIM_SPEEDS[simSpeedIdx].delay,
        weather,
        home_difficulty: homeDiff,
        away_difficulty: awayDiff,
      };
      const res = await axios.post(`${API}/games/bot-vs-bot`, payload);
      track("game_created_bot_vs_bot", {});
      navigate(`/game/${res.data.id}?role=home&spectate=true`);
    } catch {
      toast.error("Couldn't start Bot vs Bot — is the server running?");
      setStarting(false);
    }
  };

  const activeSpeed = SIM_SPEEDS[simSpeedIdx];

  return (
    <>
      <div className="text-center mb-8">
        <p className="text-xs font-bold text-emerald-400 tracking-widest uppercase mb-1">Bot vs Bot</p>
        <h1 className="text-3xl font-black">Watch them play.</h1>
        <p className="text-slate-500 text-sm mt-1">Configure the match, then sit back and watch two bots battle.</p>
      </div>

      <div className="mb-6">
        <GameSettingsPanel
          settings={settings}
          onSettingChange={handleSettingChange}
          showPitchMod
          weather={weather}
          onRerollWeather={() => setWeather(generateClientWeather())}
          // Draft flows aren't bot-aware yet — see comment in BotSetup.
          draftsSupported={false}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        {[
          { label: "Home · Bot", diff: homeDiff, setDiff: setHomeDiff },
          { label: "Away · Bot", diff: awayDiff, setDiff: setAwayDiff },
        ].map(({ label, diff, setDiff }) => (
          <div key={label} className="rounded-xl border border-slate-700/40 bg-slate-800/20 p-3.5 flex items-center gap-3">
            <span className="text-2xl select-none">🤖</span>
            <div className="min-w-0">
              <p className="text-sm font-black text-white">Random Bot</p>
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-1">{label}</p>
              <select
                value={diff}
                onChange={e => setDiff(e.target.value)}
                className="bg-slate-900 border border-slate-700 rounded text-[10px] font-black uppercase tracking-wide text-emerald-400 px-1.5 py-0.5 focus:outline-none focus:border-emerald-500"
              >
                <option value="easy">Easy</option>
                <option value="normal">Normal</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>
        ))}
      </div>

      {/* Sim Speed */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 mb-6 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Sim Speed</p>
          <span className="text-xs font-black text-emerald-400">{activeSpeed.label}</span>
        </div>

        {/* Slider */}
        <div className="space-y-3">
          <input
            type="range"
            min={0}
            max={SIM_SPEEDS.length - 1}
            step={1}
            value={simSpeedIdx}
            onChange={e => setSimSpeedIdx(Number(e.target.value))}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-emerald-500"
            style={{ background: `linear-gradient(to right, #10b981 0%, #10b981 ${simSpeedIdx / (SIM_SPEEDS.length - 1) * 100}%, #1e293b ${simSpeedIdx / (SIM_SPEEDS.length - 1) * 100}%, #1e293b 100%)` }}
          />

          {/* Tick labels */}
          <div className="flex justify-between">
            {SIM_SPEEDS.map((s, i) => (
              <button
                key={s.label}
                onClick={() => setSimSpeedIdx(i)}
                className={`flex flex-col items-center gap-0.5 transition-colors ${i === simSpeedIdx ? "text-emerald-400" : "text-slate-600 hover:text-slate-400"}`}
              >
                <span className={`text-[10px] font-black uppercase tracking-wide ${i === simSpeedIdx ? "text-emerald-400" : ""}`}>{s.label}</span>
              </button>
            ))}
          </div>
        </div>

        <p className="text-[11px] text-slate-500 text-center">{activeSpeed.desc}</p>
      </div>

      <button onClick={handleStart} disabled={starting}
        className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-sm tracking-wide transition-all disabled:opacity-60 flex items-center justify-center gap-2">
        {starting && <Loader2 size={14} className="animate-spin" />}
        {starting ? "Starting…" : "Watch Bot vs Bot →"}
      </button>
    </>
  );
}

// ─── Root export ──────────────────────────────────────────────────────────────
export default function PvPSetup({ onlineMode = false, botMode = false, botVsBotMode = false }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Nav />
      <div className="flex-1 flex flex-col pt-20 pb-16 px-4">
        <div className={`mx-auto w-full ${onlineMode ? "max-w-4xl" : "max-w-2xl"} py-8`}>
          {botVsBotMode ? <BotVsBotSetup /> : botMode ? <BotSetup /> : onlineMode ? <OnlineSetup /> : <LocalSetup />}
          <p className="text-center mt-8 text-xs text-slate-700 tracking-wide italic">
            Every card deserves its day.
          </p>
        </div>
      </div>
    </div>
  );
}
